import React, { useEffect, useState, useMemo } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Table,
  Pagination,
  Spinner,
} from 'react-bootstrap';
import { FaSearch, FaFileExcel, FaExclamationCircle, FaEraser } from 'react-icons/fa';
import { default as Selector } from 'react-select';
import Header from '~/Components/Header/Header';
import {
  consultarRelatorioOcorrencias,
  listarUsuariosAtivosOcorrencias,
  listarClientesPorGeOcorrencias,
  listarGruposEmpresaTransferenciaOcorrencias,
} from '~/Services/Service';
import { toast } from 'react-toastify';
import { salvarEmExcel } from '~/Utils/Helpers';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  ORDENS_OCORRENCIAS_OPTIONS,
  PAGE_SIZE_DEFAULT_OCORRENCIAS,
} from '~/constant';

// Datas default: 1o dia do mes corrente -> hoje (R13).
const primeiroDiaDoMes = () => {
  const hoje = new Date();
  return new Date(hoje.getFullYear(), hoje.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
};
const hojeISO = () => new Date().toISOString().slice(0, 10);

const filtrosIniciais = () => ({
  dataInicio: primeiroDiaDoMes(),
  dataFim: hojeISO(),
  codigoGrupoEmpresa: null,
  codigoUsuario: null,
  codigoFamilia: null,
  codigoConta: null,
  titulo: '',
  ordem: ORDENS_OCORRENCIAS_OPTIONS[0].value,
  incluirTexto: false,
});

const OcorrenciasContas = () => {
  const [filtros, setFiltros] = useState(filtrosIniciais);
  const [ges, setGes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);

  const [resultado, setResultado] = useState(null); // { items, pagina, tamanho_pagina, total_items, total_paginas }
  const [pagina, setPagina] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [buscou, setBuscou] = useState(false);

  // Combos GE + usuarios (uma vez).
  useEffect(() => {
    listarGruposEmpresaTransferenciaOcorrencias()
      .then(data => setGes(Array.isArray(data) ? data : []))
      .catch(() => setGes([]));
    listarUsuariosAtivosOcorrencias()
      .then(data => setUsuarios(Array.isArray(data) ? data : []))
      .catch(() => setUsuarios([]));
  }, []);

  // Auto-selecao de GE quando ha exatamente uma (R18).
  useEffect(() => {
    if (ges.length === 1 && filtros.codigoGrupoEmpresa == null) {
      setFiltros(prev => ({ ...prev, codigoGrupoEmpresa: ges[0].codigo }));
    }
  }, [ges]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cascading GE -> Cliente (R3/ADR-005): ao mudar GE, recarrega clientes e
  // reseta o cliente selecionado.
  useEffect(() => {
    setFiltros(prev => ({ ...prev, codigoConta: null }));
    setClientes([]);
    if (filtros.codigoGrupoEmpresa != null) {
      listarClientesPorGeOcorrencias(filtros.codigoGrupoEmpresa)
        .then(data => setClientes(Array.isArray(data) ? data : []))
        .catch(() => setClientes([]));
    }
  }, [filtros.codigoGrupoEmpresa]); // eslint-disable-line react-hooks/exhaustive-deps

  const opcoesGe = useMemo(
    () =>
      ges.map(g => ({
        value: g.codigo,
        label: g.apelido ? `${g.codigo} - ${g.apelido}` : `${g.codigo}`,
      })),
    [ges]
  );
  const opcoesUsuario = useMemo(
    () =>
      usuarios.map(u => ({
        value: u.codigo_usuario,
        label: `${u.codigo_usuario} - ${u.nome_usuario || ''}`.trim(),
      })),
    [usuarios]
  );
  const opcoesCliente = useMemo(
    () =>
      clientes.map(c => ({
        value: c.codigo_conta,
        label: `${c.codigo_conta} - ${c.apelido_conta || ''}`.trim(),
      })),
    [clientes]
  );

  const setCampo = (campo, valor) =>
    setFiltros(prev => ({ ...prev, [campo]: valor }));

  const validar = () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      toast.error('Período inválido!');
      return false;
    }
    if (filtros.dataFim < filtros.dataInicio) {
      toast.error('Data final deve ser maior ou igual a inicial.');
      return false;
    }
    if (filtros.codigoConta != null && filtros.codigoGrupoEmpresa == null) {
      toast.error('Selecione um Grupo de Empresa primeiro.');
      return false;
    }
    return true;
  };

  const buscar = async (paginaAlvo = 1) => {
    if (!validar()) return;
    setLoading(true);
    setBuscou(true);
    try {
      const data = await consultarRelatorioOcorrencias({
        ...filtros,
        pagina: paginaAlvo,
        tamanhoPagina: PAGE_SIZE_DEFAULT_OCORRENCIAS,
      });
      setResultado(data);
      setPagina(paginaAlvo);
    } catch (err) {
      setResultado(null);
      toast.error(
        err && err.mensagem
          ? err.mensagem
          : err && err.message
          ? err.message
          : 'Não foi possível carregar o relatório.'
      );
    } finally {
      setLoading(false);
    }
  };

  const exportar = async () => {
    if (!validar()) return;
    setExportando(true);
    try {
      const payload = {
        data_inicio: filtros.dataInicio,
        data_fim: filtros.dataFim,
        codigo_grupo_empresa: filtros.codigoGrupoEmpresa,
        codigo_usuario: filtros.codigoUsuario,
        codigo_familia: filtros.codigoFamilia,
        codigo_conta: filtros.codigoConta,
        titulo: filtros.titulo ? filtros.titulo.trim() : null,
        ordem: filtros.ordem,
        incluir_texto: filtros.incluirTexto,
      };
      await salvarEmExcel(payload, '/relatorios/ocorrencias/exportar-excel');
      toast.success('Relatório exportado com sucesso!');
    } catch (err) {
      toast.error(
        err && err.mensagem
          ? err.mensagem
          : 'Refine o período / filtros — mais de 50000 linhas.'
      );
    } finally {
      setExportando(false);
    }
  };

  const limpar = () => {
    setFiltros(filtrosIniciais());
    setResultado(null);
    setBuscou(false);
    setPagina(1);
  };

  const items = resultado && resultado.items ? resultado.items : [];
  const totalPaginas = resultado ? resultado.total_paginas || 0 : 0;

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const maxVisible = 5;
    const start = Math.max(1, pagina - Math.floor(maxVisible / 2));
    const end = Math.min(totalPaginas, start + maxVisible - 1);
    const numeros = [];
    for (let p = start; p <= end; p++) {
      numeros.push(
        <Pagination.Item key={`pag-${p}`} active={p === pagina} onClick={() => buscar(p)}>
          {p}
        </Pagination.Item>
      );
    }
    return (
      <Row className="justify-content-end">
        <Col xs="auto">
          <Pagination className="d-inline-flex mb-0">
            <Pagination.First onClick={() => buscar(1)} disabled={pagina === 1} />
            <Pagination.Prev onClick={() => buscar(pagina - 1)} disabled={pagina === 1} />
            {numeros}
            <Pagination.Next
              onClick={() => buscar(pagina + 1)}
              disabled={pagina === totalPaginas}
            />
            <Pagination.Last
              onClick={() => buscar(totalPaginas)}
              disabled={pagina === totalPaginas}
            />
          </Pagination>
        </Col>
      </Row>
    );
  };

  const formatarData = valor => {
    if (!valor) return '';
    const d = new Date(valor);
    if (isNaN(d.getTime())) return valor;
    return d.toLocaleDateString('pt-BR');
  };

  return (
    <div>
      <Header title="Lista de Ocorrências de Contas" />

      <div className="container-fluid bg-white p-3">
        <Row>
          <Col xs={12} sm={6} md={2}>
            <Form.Group>
              <Form.Label>Data Inicial *</Form.Label>
              <Form.Control
                type="date"
                value={filtros.dataInicio}
                onChange={e => setCampo('dataInicio', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={2}>
            <Form.Group>
              <Form.Label>Data Final *</Form.Label>
              <Form.Control
                type="date"
                value={filtros.dataFim}
                onChange={e => setCampo('dataFim', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group>
              <Form.Label>Grupo de Empresa</Form.Label>
              <Selector
                options={opcoesGe}
                value={opcoesGe.find(o => o.value === filtros.codigoGrupoEmpresa) || null}
                onChange={op => setCampo('codigoGrupoEmpresa', op ? op.value : null)}
                styles={customStyles}
                components={{ DropdownIndicator }}
                placeholder="Todos..."
                isClearable
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group>
              <Form.Label>Usuário</Form.Label>
              <Selector
                options={opcoesUsuario}
                value={opcoesUsuario.find(o => o.value === filtros.codigoUsuario) || null}
                onChange={op => setCampo('codigoUsuario', op ? op.value : null)}
                styles={customStyles}
                components={{ DropdownIndicator }}
                placeholder="Todos..."
                isClearable
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} sm={6} md={4}>
            <Form.Group>
              <Form.Label>Cliente</Form.Label>
              <Selector
                options={opcoesCliente}
                value={opcoesCliente.find(o => o.value === filtros.codigoConta) || null}
                onChange={op => setCampo('codigoConta', op ? op.value : null)}
                styles={customStyles}
                components={{ DropdownIndicator }}
                placeholder={
                  filtros.codigoGrupoEmpresa == null
                    ? 'Selecione um Grupo de Empresa primeiro'
                    : 'Todos...'
                }
                isClearable
                isDisabled={filtros.codigoGrupoEmpresa == null}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={2}>
            <Form.Group>
              <Form.Label>Empreendimento (cód.)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                placeholder="Código família..."
                value={filtros.codigoFamilia != null ? filtros.codigoFamilia : ''}
                onChange={e =>
                  setCampo(
                    'codigoFamilia',
                    e.target.value ? parseInt(e.target.value, 10) : null
                  )
                }
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Group>
              <Form.Label>Título</Form.Label>
              <Form.Control
                type="text"
                maxLength={50}
                placeholder="Buscar por título..."
                value={filtros.titulo}
                onChange={e => setCampo('titulo', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Group>
              <Form.Label>Ordenação</Form.Label>
              <Selector
                options={ORDENS_OCORRENCIAS_OPTIONS}
                value={
                  ORDENS_OCORRENCIAS_OPTIONS.find(o => o.value === filtros.ordem) ||
                  ORDENS_OCORRENCIAS_OPTIONS[0]
                }
                onChange={op =>
                  setCampo('ordem', op ? op.value : ORDENS_OCORRENCIAS_OPTIONS[0].value)
                }
                styles={customStyles}
                components={{ DropdownIndicator }}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="align-items-end">
          <Col xs={12} sm={6} md={3}>
            <Form.Group className="mb-0">
              <Form.Check
                type="checkbox"
                id="chk-incluir-texto"
                label="Exibir texto da ocorrência"
                checked={filtros.incluirTexto}
                onChange={e => setCampo('incluirTexto', e.target.checked)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Button
              type="button"
              variant="primary"
              className="w-100"
              onClick={() => buscar(1)}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="mr-2" />
                  Buscando...
                </>
              ) : (
                <>
                  <FaSearch className="mr-2" />
                  Buscar
                </>
              )}
            </Button>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Button
              type="button"
              variant="success"
              className="w-100"
              onClick={exportar}
              disabled={exportando || items.length === 0}
            >
              {exportando ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="mr-2" />
                  Exportando...
                </>
              ) : (
                <>
                  <FaFileExcel className="mr-2" />
                  Exportar Excel
                </>
              )}
            </Button>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Button type="button" variant="outline-secondary" className="w-100" onClick={limpar}>
              <FaEraser className="mr-2" />
              Limpar
            </Button>
          </Col>
        </Row>
      </div>

      {items.length > 0 ? (
        <>
          <div className="container-fluid bg-white p-3 mt-3">{renderPaginacao()}</div>
          <div className="container-fluid bg-white table-responsive">
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th style={{ whiteSpace: 'nowrap' }}>GE</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Conta</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Cliente</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Data</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Tipo</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Título</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Usuário</th>
                  {filtros.incluirTexto && <th style={{ whiteSpace: 'nowrap' }}>Ocorrência</th>}
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr
                    key={`oco-${item.codigo_grupo_empresa}-${item.codigo_conta}-${item.data_ocorrencia}-${item.titulo}`}
                  >
                    <td>{item.codigo_grupo_empresa}</td>
                    <td>{item.codigo_conta}</td>
                    <td>{item.apelido_conta}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatarData(item.data_ocorrencia)}</td>
                    <td>{item.tipo}</td>
                    <td>{item.titulo}</td>
                    <td>{item.nome_usuario}</td>
                    {filtros.incluirTexto && <td>{item.texto_ocorrencia}</td>}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="container-fluid bg-white p-3">{renderPaginacao()}</div>
        </>
      ) : (
        !loading &&
        buscou && (
          <div className="container-fluid bg-white p-5 mt-3 text-center">
            <Row>
              <Col>
                <FaExclamationCircle size={48} className="text-muted mb-3" />
                <h5 className="text-muted">Nenhum registro encontrado.</h5>
                <p className="text-muted">Ajuste os filtros e tente novamente.</p>
              </Col>
            </Row>
          </div>
        )
      )}
    </div>
  );
};

export default OcorrenciasContas;
